import { NgClass } from "@angular/common";
import { Component, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy, inject } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbAccordionBody, NgbAccordionButton, NgbAccordionCollapse, NgbAccordionDirective, NgbAccordionHeader, NgbAccordionItem, NgbAccordionToggle, NgbCollapse, NgbDropdownModule, NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from "@ng-bootstrap/ng-bootstrap";
import { CompanyModalService } from "company/services/company-modal.service";
import { intersectionBy } from "lodash";
import { CheckboxInputComponent } from "rl-common/components/checkbox-input/checkbox-input.component";
import { ChecklistControlComponent } from "rl-common/components/checklist-control/checklist-control.component";
import { ChipComponent } from "rl-common/components/chip/chip.component";
import { IParty } from "rl-common/components/contacts/models/i-party";
import { FormTableControlDirective } from "rl-common/components/form-table/form-table-control.directive";
import { FormTableGroupComponent } from "rl-common/components/form-table/form-table-group/form-table-group.component";
import { FormTableRowComponent } from "rl-common/components/form-table/form-table-row/form-table-row.component";
import { GridDataSourceBuilder } from "rl-common/components/grid/datasource/builders/grid-datasource-builder";
import { CommonGridDataSource } from "rl-common/components/grid/datasource/common-grid.datasource";
import { IGridFetchResults } from "rl-common/components/grid/datasource/grid-datasource.models";
import { CellTemplateDirective } from "rl-common/components/grid/directives/cell-template.directive";
import { GridRowHoverOpsDirective } from "rl-common/components/grid/directives/grid-row-hover-ops.directive";
import { GridTableComponent } from "rl-common/components/grid/grid-table/grid-table.component";
import { GridColumn } from "rl-common/components/grid/models/grid-column";
import { GridOptions } from "rl-common/components/grid/models/grid-options";
import { GridRowStyles } from "rl-common/components/grid/models/grid-row-styles";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";
import { ICharacteristicTemplate } from "rl-common/models/i-characteristic-template";
import { ComponentChanges } from "rl-common/models/i-component-change";
import { MultipleIndicator } from "rl-common/models/multiple-indicator";
import { TrimWhitespacePipe } from "rl-common/pipes/trim-whitespace.pipe";
import { CharTypeId, MaxInt } from "rl-common/rl-common.consts";
import { AccountingProcessesService } from "rl-common/services/accounting-processes/accounting-processes.service";
import { AccountingProcessActiveIndicator } from "rl-common/services/accounting-processes/models/accounting-process-active-indicator";
import { IAccountingContact } from "rl-common/services/accounting-processes/models/i-accounting-contact";
import { IAccountingProcessAccount } from "rl-common/services/accounting-processes/models/i-accounting-process-account";
import { IAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group";
import { IAccountingProcessAccountGroupRuleSet } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group-rule-set";
import { IAccountingProcessAccountResult } from "rl-common/services/accounting-processes/models/i-accounting-process-account-result";
import { ICreateAccountingProcessAccount } from "rl-common/services/accounting-processes/models/i-create-accounting-process-account";
import { ICreateAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-create-accounting-process-account-group";
import { GrowlerService } from "rl-common/services/growler.service";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { PromptService } from "rl-common/services/prompt/prompt.service";
import { SessionService } from "rl-common/services/session.service";
import { Observable, of, Subscription } from "rxjs";
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap, take, tap } from "rxjs/operators";
import { AccountGroupRuleSetsComponent, IRuleSetsLoaded } from "../account-group-rule-sets/account-group-rule-sets.component";

export interface ICoaForm {
	id: FormControl<string>;
	name: FormControl<string>;
	accountId: FormControl<string>;
	notes: FormControl<string>;
	xref: FormControl<string>;
	fxGainLossInd: FormControl<boolean>;
}
export interface ICoaGroupForm {
	id: FormControl<string>;
	groupName: FormControl<string>;
	dealTemplates: FormControl<ICharacteristicTemplate[]>;
	partyId: FormControl<number>;
	defaultAccountId: FormControl<string>;
}

export const enum COAFormTypes {
	Account = "account",
	AccountGroup = "account-group"
}

export enum COAFormTypesIndex {
	Account = 1,
	AccountGroup = 2
}

@Component({
	selector: "rl-chart-of-accounts",
	templateUrl: "./chart-of-accounts.component.html",
	styleUrls: ["./chart-of-accounts.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, FormsModule, NgClass, NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, GridTableComponent, GridRowHoverOpsDirective, NgbAccordionDirective, NgbAccordionItem, NgbAccordionHeader, NgbAccordionToggle, NgbAccordionButton, NgbCollapse, NgbAccordionCollapse, NgbAccordionBody, FormTableRowComponent, FormTableControlDirective, TextInputComponent, FormTableGroupComponent, AccountGroupRuleSetsComponent, NgbNavOutlet, TrimWhitespacePipe, CheckboxInputComponent, CellTemplateDirective, ChipComponent, ChecklistControlComponent, NgbDropdownModule]
})
export class ChartOfAccountsComponent implements OnInit, OnChanges, OnDestroy {
	private readonly _accountingProcessesService = inject(AccountingProcessesService);
	private readonly _modalService = inject(CompanyModalService);
	private readonly _injector = inject(Injector);
	private readonly _gridDataSourceBuilder = inject(GridDataSourceBuilder);
	private readonly _formBuilder = inject(FormBuilder);
	private readonly _oneConfig = inject(OneConfigService);
	private readonly _growler = inject(GrowlerService);
	private readonly _promptService = inject(PromptService);
	private readonly _session = inject(SessionService);


	@Input()
	contact: IAccountingContact;

	@Input()
	contactsAreLoading: boolean;

	@Output()
	onAccountsLoad = new EventEmitter<IAccountingProcessAccountResult[]>();

	@ViewChild("accordion")
	accordion: NgbAccordionDirective;

	@ViewChildren(AccountGroupRuleSetsComponent)
	accountGroupRuleSets: QueryList<AccountGroupRuleSetsComponent>;

	formType: string = COAFormTypes.Account;
	coaOptions = [
		{
			id: COAFormTypes.Account,
			value: "Account"
		},
		{
			id: COAFormTypes.AccountGroup,
			value: "Account Group"
		}
	];

	private readonly columns: GridColumn<IAccountingProcessAccountResult>[] = [
		{
			key: "id",
			headerName: "ID",
			renderer: "uuid",
			width: "auto",
			getCellData: (x) => x.account.id
		},
		{
			key: "fxGainLossInd",
			headerName: "FX Gain/Loss",
			renderer: "fxGainLossInd",
			width: "auto",
			getCellData: (not => not)
		},
		{
			key: "name",
			headerName: "Account Name",
			renderer: "text",
			width: "auto",
			getCellData: (x) => x.account.name
		},
		{
			key: "accountId",
			headerName: "Account ID",
			renderer: "text",
			width: "auto",
			getCellData: (x) => x.account.accountId
		},
		{
			key: "xref",
			headerName: "XREF",
			renderer: "text",
			width: "auto",
			getCellData: (x) => x.account.xref
		},
		{
			key: "notes",
			headerName: "Account Notes",
			renderer: "text",
			width: "auto",
			getCellData: (x) => x.account.notes
		},
		{
			key: "alliantAccountGuid",
			headerName: "Synced Account",
			renderer: "uuid",
			width: "auto",
			getCellData: (x) => x.account.alliantAccountGuid
		},
		{
			key: "alliantContactGuid",
			headerName: "Synced Contact",
			renderer: "uuid",
			width: "auto",
			getCellData: (x) => x.account.alliantContactGuid
		},
		{
			key: "sequenceId",
			headerName: "Sequence ID",
			renderer: "text",
			width: "auto",
			getCellData: (x) => x.account.alliantSequenceId
		},
		{
			key: "syncResult",
			headerName: "Sync Result",
			renderer: "text",
			width: "300px",
			getCellData: (x) => x.account.syncResult
		},
	];

	gridOptions: GridOptions<unknown> = {
		defaultGetCellDataFn: (column, doc) => doc
	};

	accountsDataSource: CommonGridDataSource<IAccountingProcessAccountResult> = this._gridDataSourceBuilder.commonGridDataSource<IAccountingProcessAccountResult>(row => row.account.id)
		.setPaging({ pageSize: MaxInt.Num })
		.setColumns(this.columns)
		.withGetRowStylesFn(row => {
			if (row.account.activeIndicator !== this.activeIndicator) {
				return of([GridRowStyles.Inactive]);
			}
			return of([]);
		})
		.withFetchFn((_ds) => this._accountingProcessesService.getAccounts(this.contact.recordId).pipe(
			tap((results) => {
				this.defaultAccounts = results.map(r => [r.account.id, r.account.name]);
			}),
			map((results) => ({ rowCount: results.length, rowData: results } as IGridFetchResults<IAccountingProcessAccountResult>))));

	accountsForm: FormGroup<ICoaForm>;
	accountGroupsForm: FormGroup<ICoaGroupForm>;

	get isEditingExistingAccount() {
		return !!this.accountsForm?.controls?.id?.value;
	}

	get isEditingExistingAccountGroup() {
		return !!this.accountGroupsForm?.controls?.id?.value;
	}

	get formBorderClass() {
		if (this.isEditingExistingAccount) {
			return `warning`;
		}
		return ``;
	}

	arePostingsGenerated = false;
	isSubmitting = false;
	isSyncing = new Set<string>();
	isDeleting = new Set<string>();
	isDeletingGroup = new Set<string>();
	isRlAdmin = false;
	activeIndicator = AccountingProcessActiveIndicator.Active;
	account: IAccountingProcessAccount;
	accountGroup: IAccountingProcessAccountGroup;
	accountGroups: IAccountingProcessAccountGroup[] = [];
	sortedAccountGroups: IAccountingProcessAccountGroup[] = [];

	coaFormTypesIndex = COAFormTypesIndex;
	allDealTemplates: ICharacteristicTemplate[] = [];
	selectedDealPartyCollection: IParty[] = [];
	dealPartyCollection: { [dealTemplateIds: string]: IParty[]; } = {};
	defaultAccounts: [string, string][] = []; //[ID, NAME]

	private readonly _subscriptions: Subscription[] = [];

	loadAccountGroups$: Observable<IAccountingProcessAccountGroup[]>;

	activeTabId = 1;

	/** per Account Group, what the Add/Edit Rule Set modal needs: keyed by group id */
	ruleSetsByGroup = new Map<string, IRuleSetsLoaded>();

	ngOnInit(): void {
		this.allDealTemplates = this._oneConfig.getTemplates(CharTypeId.Deal);
		this.buildAccountsForm();
		this.buildAccountGroupsForm();

		const rowDataSub = this.accountsDataSource.rowData$.pipe(
			filter(rowData => !!rowData)
		).subscribe((rowData) => {
			this.onAccountsLoad.next(rowData);
		});

		const isAdminSub = this._session.isRlAdmin$.pipe(
			distinctUntilChanged()
		).subscribe(isRlAdmin => {
			this.setIsRlAdmin(isRlAdmin);
		});

		this._subscriptions.push(rowDataSub, isAdminSub);
	}

	ngOnChanges(changes: ComponentChanges<this>): void {
		if (changes.contact && changes.contact.previousValue?.recordId !== changes.contact.currentValue?.recordId) {
			this.refreshAccountsAndGroups();
			this.buildAccountsForm();
			this.buildAccountGroupsForm();
		}
	}

	refreshAccountsAndGroups() {
		this.loadAccounts();
		this.loadAccountGroups();
	}

	coaOptionChanged() {
		this.activeTabId = (this.formType === COAFormTypes.Account) ? COAFormTypesIndex.Account : COAFormTypesIndex.AccountGroup;
		this.clearForm();
	}

	coaTabChanged() {
		this.formType = this.activeTabId === this.coaFormTypesIndex.Account ? COAFormTypes.Account : COAFormTypes.AccountGroup;
		this.clearForm();
	}

	private clearForm() {
		if (this.formType === COAFormTypes.Account) {
			this.buildAccountsForm();
		} else if (this.formType === COAFormTypes.AccountGroup) {
			this.buildAccountGroupsForm();
		}
	}

	buildAccountsForm(account: IAccountingProcessAccount = null) {
		this.account = account;

		if (account) {
			this.accountsForm = this._formBuilder.group({
				id: new FormControl<string>(account.id, [Validators.required]),
				name: new FormControl<string>(account.name, [Validators.required, this.duplicateAccountNameValidator]),
				accountId: new FormControl<string>(account.accountId, [Validators.required]),
				notes: new FormControl<string>(account.notes),
				xref: new FormControl<string>(account.xref),
				fxGainLossInd: new FormControl<boolean>(account.fxGainLossInd, [this.duplicateFxGainLossAccount])
			});
		} else {
			this.accountsForm = this._formBuilder.group({
				id: new FormControl<string>(null),
				name: new FormControl<string>("", [Validators.required, this.duplicateAccountNameValidator]),
				accountId: new FormControl<string>(null, [Validators.required]),
				notes: new FormControl<string>(""),
				xref: new FormControl<string>(""),
				fxGainLossInd: new FormControl<boolean>(false)
			});
		}
	}

	buildAccountGroupsForm(accountGroup: IAccountingProcessAccountGroup = null) {
		this.accountGroup = accountGroup;
		this.selectedDealPartyCollection = [];

		if (accountGroup) {
			this.accountGroupsForm = this._formBuilder.group({
				id: new FormControl<string>(accountGroup.id, [Validators.required]),
				groupName: new FormControl<string>(accountGroup.groupName, [Validators.required, this.duplicateAccountGroupNameValidator]),
				dealTemplates: new FormControl<ICharacteristicTemplate[]>(accountGroup.dealTemplates, [Validators.required]),
				partyId: new FormControl<number>(accountGroup.partyId, [Validators.required]),
				defaultAccountId: new FormControl<string>(accountGroup.defaultAccountId, [Validators.required])
			});
		} else {
			this.accountGroupsForm = this._formBuilder.group({
				id: new FormControl<string>(null),
				groupName: new FormControl<string>("", [Validators.required, this.duplicateAccountGroupNameValidator]),
				dealTemplates: new FormControl<ICharacteristicTemplate[]>(null, [Validators.required]),
				partyId: new FormControl<number>(null, [Validators.required]),
				defaultAccountId: new FormControl<string>(null, [Validators.required])
			});
		}

		const dealTemplateChangedSub = this.accountGroupsForm.controls.dealTemplates.valueChanges.subscribe(templates => {
			if (templates) {
				this.getSelectedDealPartyCollectionMult(templates);
			}
			this.accountGroupsForm.controls.partyId.setValue(null);
		});

		if (accountGroup?.dealTemplates) {
			this.getSelectedDealPartyCollectionMult(accountGroup.dealTemplates);
		}

		this._subscriptions.push(dealTemplateChangedSub);
	}

	getSelectedDealPartyCollectionMult(templates: ICharacteristicTemplate[]) {
		if (!templates) {
			return;
		}
		const allTempParties = templates.map(t => this._oneConfig.getParties(CharTypeId.Deal, t.templateID));
		const intByPartyId = intersectionBy(...allTempParties, "partyID");

		this.selectedDealPartyCollection = intByPartyId.filter(p => p.multipleIndicator === MultipleIndicator.Singular);

		if (this.selectedDealPartyCollection.length === 0) {
			this.accountGroupsForm.controls.partyId.setValue(null);
		}
	}

	duplicateAccountNameValidator = (fc: FormControl<string>) => {
		if (!fc.value) {
			return null;
		}

		const currentAccountNames = (this.accountsDataSource.rowData$.value ?? []).map(x => x.account.name.toLowerCase());
		const cleanedAccountName = fc.value.trim().toLowerCase();
		const isDuplicateWhenEditing = this.isEditingExistingAccount &&
			currentAccountNames.some(an => an === cleanedAccountName && an !== this.account?.name.trim().toLowerCase());
		const isDuplicateWhenCreating = !this.isEditingExistingAccount &&
			currentAccountNames.includes(cleanedAccountName);

		if (isDuplicateWhenEditing || isDuplicateWhenCreating) {
			return { "accountNameDuplicate": true };
		}

		return null;
	};

	isThereAnFxGainLossAccountAlready() {
		const otherAccounts = this.accountsDataSource.rowData$?.value
			?.filter(x => !this.isEditingExistingAccount || x.account.accountId !== this.accountsForm?.controls.accountId.value)
			.map(x => x.account.fxGainLossInd);
		return otherAccounts?.find(x => x);
	}

	duplicateFxGainLossAccount = (fc: FormControl<boolean>) => {
		if (fc.value && this.isThereAnFxGainLossAccountAlready()) {
			return { "fxGainLossDuplicate": true };
		}

		return null;
	}

	duplicateAccountGroupNameValidator = (fc: FormControl<string>) => {
		if (!fc.value) {
			return null;
		}

		const currentAccountGroupNames = this.accountGroups.map(x => x.groupName.toLowerCase());
		const cleanedAccountGroupName = fc.value.trim().toLowerCase();
		const isDuplicateWhenEditing = this.isEditingExistingAccountGroup &&
			currentAccountGroupNames.some(an => an === cleanedAccountGroupName && an !== this.accountGroup.groupName.trim().toLowerCase());
		const isDuplicateWhenCreating = !this.isEditingExistingAccountGroup &&
			currentAccountGroupNames.includes(cleanedAccountGroupName);

		if (isDuplicateWhenEditing || isDuplicateWhenCreating) {
			return { "accountGroupNameDuplicate": true };
		}

		return null;
	};

	loadAccounts() {
		this.isDeleting.clear();
		const sub = this.accountsDataSource.fetchRows().subscribe();
		this._subscriptions.push(sub);
	}

	loadAccountGroups() {
		this.loadAccountGroups$ = this._accountingProcessesService.getAccountGroups(this.contact.recordId).pipe(
			map(dbgroups => {
				const accountGroups: IAccountingProcessAccountGroup[] = [];
				dbgroups.forEach(gdb => {
					const dealTemplates = gdb.dealTemplateIds.map(t => this._oneConfig.getTemplate(CharTypeId.Deal, t)).filter(t => !!t);
					accountGroups.push({
						id: gdb.id,
						groupName: gdb.groupName,
						dealTemplates,
						partyId: gdb.partyId,
						defaultAccountId: gdb.defaultAccountId,
						contactId: gdb.contactId,
						ruleCount: gdb.ruleCount,
						isAssociated: gdb.isAssociated
					} as IAccountingProcessAccountGroup);
				});
				return accountGroups;
			}),
			tap(groups => {
				this.accountGroups = groups;
				this.sortAccountGroups();
			})
		);
		this._subscriptions.push(this.loadAccountGroups$.subscribe());
	}

	private sortAccountGroups() {
		this.sortedAccountGroups = this.accountGroups
			.map(g => ({
				...g,
				groupName: g.groupName.trim()
			}))
			.sort((a, b) => a.groupName.localeCompare(b.groupName))
			.map(g => ({
				...g,
				groupName: g.groupName
			}));
	}

	saveAccount() {
		this.isSubmitting = true;
		const account: ICreateAccountingProcessAccount = {
			id: this.accountsForm.controls.id.value,
			contactRecordId: this.contact.recordId,
			name: this.accountsForm.controls.name.value,
			accountId: this.accountsForm.controls.accountId.value,
			notes: this.accountsForm.controls.notes.value,
			xref: this.accountsForm.controls.xref.value,
			fxGainLossInd: this.accountsForm.controls.fxGainLossInd.value
		};

		const sub = this._accountingProcessesService.upsertAccount(account).pipe(
			switchMap(() => this.accountsDataSource.fetchRows()),
			catchError(err => {
				const message = err?.error?.message ?? `There was an error updating account`;
				this._growler.error().growl(message);
				throw err;
			}),
			finalize(() => this.isSubmitting = false)
		).subscribe(() => {
			this.buildAccountsForm();
		});

		this._subscriptions.push(sub);
	}

	saveAccountGroup() {
		this.isSubmitting = true;
		const accountGroup: ICreateAccountingProcessAccountGroup = {
			id: this.accountGroupsForm.controls.id.value,
			groupName: this.accountGroupsForm.controls.groupName.value,
			dealTemplateIds: this.accountGroupsForm.controls.dealTemplates.value.map(t => t.templateID),
			partyId: this.accountGroupsForm.controls.partyId.value,
			defaultAccountId: this.accountGroupsForm.controls.defaultAccountId.value,
			contactId: this.contact.recordId
		};

		const sub = this._accountingProcessesService.upsertAccountGroup(accountGroup).pipe(
			switchMap(() => this.loadAccountGroups$),
			catchError(err => {
				const message = err?.error?.message ?? `There was an error updating account group`;
				this._growler.error().growl(message);
				throw err;
			}),
			finalize(() => this.isSubmitting = false)
		).subscribe(() => {
			this.buildAccountGroupsForm();
		});

		this._subscriptions.push(sub);
	}

	removeTemplate(item: ICharacteristicTemplate) {
		let values = this.accountGroupsForm.controls.dealTemplates.value;
		values = values.filter((x) => x.templateID != item.templateID);
		this.accountGroupsForm.controls.dealTemplates.setValue(values);
	}

	syncAccount(account: IAccountingProcessAccount) {
		this.isSyncing.add(account.id);
		const sub = this._accountingProcessesService.syncAccount(this.contact.recordId, account.id).pipe(
			switchMap((success) => this.accountsDataSource.fetchRows().pipe(map(() => success))),
			catchError(err => {
				const message = err?.error?.message ?? `There was an error synchronizing account`;
				this._growler.error().growl(message);
				throw err;
			}),
			finalize(() => this.isSyncing.delete(account.id))
		).subscribe((success) => {
			if (success) {
				const message = `Account sync succeeded.`;
				this._growler.success().growl(message);
			} else {
				const message = `Account sync failed.`;
				this._growler.error().growl(message);
			}
		});

		this._subscriptions.push(sub);
	}

	isFormEmpty(form: FormGroup) {
		return Object.values(form.controls).every(control => {
			return control.value === null || control.value === "";
		});
	}

	editAccount(account: IAccountingProcessAccount, cancelDialog: TemplateRef<unknown>) {
		this.arePostingsGenerated = account.fxGainLossInd && account.hasBeenPostedTo;
		if (!this.accountsForm.dirty) {
			this.buildAccountsForm(account);
			return;
		}

		const isFormEmpty = this.isFormEmpty(this.accountsForm);

		if (!isFormEmpty) {
			const sub = this._promptService.confirmMini("Confirmation", cancelDialog)
				.subscribe(confirm => {
					if (confirm) {
						this.buildAccountsForm(account);
					}
				});

			this._subscriptions.push(sub);
		} else {
			this.buildAccountsForm(account);
		}
	}

	editGroup(group: IAccountingProcessAccountGroup, cancelDialog: TemplateRef<unknown>) {
		if (!this.accountGroupsForm.dirty) {
			this.buildAccountGroupsForm(group);
			return;
		}

		const isFormEmpty = this.isFormEmpty(this.accountGroupsForm);

		if (!isFormEmpty) {
			const sub = this._promptService.confirmMini("Confirmation", cancelDialog)
				.subscribe(confirm => {
					if (confirm) {
						this.buildAccountGroupsForm(group);
					}
				});

			this._subscriptions.push(sub);
		} else {
			this.buildAccountGroupsForm(group);
		}
	}

	// Rule add/edit lives on rl-account-group-rule-sets: it needs nothing from this component now
	// that the Set, not the rule, carries the account.
	addOrEditAccountGroupRuleSet(group: IAccountingProcessAccountGroup, accountGroupRuleSet: IAccountingProcessAccountGroupRuleSet = null) {
		// nothing has been emitted yet if the group's accordion has never been expanded, in which
		// case there are no Sets to collide with and the first one starts the sequence
		const loaded = this.ruleSetsByGroup.get(group.id);

		const sub = this.accountsDataSource.rowData$.pipe(
			take(1),
			map(accountRows => accountRows.map(a => a.account)),
			switchMap(accounts => this._modalService.buildAccountGroupRuleSet(accounts, group, accountGroupRuleSet, loaded?.maxSequenceNumber ?? 0, loaded?.existingNames ?? [], this._injector)),
			filter(accountGroupRuleSetResult => !!accountGroupRuleSetResult),
			switchMap(accountGroupRuleSetResult => this._accountingProcessesService.upsertAccountGroupRuleSet(accountGroupRuleSetResult.accountGroupRuleSet).pipe(
				tap(id => {
					if (id) {
						const message = accountGroupRuleSet ? `Rule Set successfully updated` : `Rule Set added to group: "${group.groupName}"`;
						this._growler.success().growl(message);
						this.accountGroupRuleSets.find(agrs => agrs.groupId === group.id)?.loadRuleSets();
					}
				}),
				catchError(err => {
					const message = err?.error?.message ?? `Error adding/editing Rule Set`;
					this._growler.error().growl(message);
					throw err;
				})
			))
		).subscribe();

		this._subscriptions.push(sub);
	}

	setRuleSetsLoaded(event: IRuleSetsLoaded) {
		this.ruleSetsByGroup.set(event.groupId, event);
	}

	deleteAccount(accountResult: IAccountingProcessAccountResult, confirmDeleteDialog: TemplateRef<unknown>) {
		if (accountResult.creditRefs.length > 0 || accountResult.debitRefs.length > 0) {
			const message = `${accountResult.account.name} cannot be deleted because it is referenced by one or more accounting processes.`;
			this._promptService.alert("Cannot Delete Account", message);
		} else if (accountResult.account.alliantAccountGuid) {
			const message = `${accountResult.account.name} cannot be deleted because it has been synchronized with Alliant.`;
			this._promptService.alert("Cannot Delete Account", message);
		} else {
			const sub = this._promptService.confirmMini("Confirmation", confirmDeleteDialog, accountResult.account).pipe(
				filter(confirm => confirm),
				tap(() => this.isDeleting.add(accountResult.account.id)),
				switchMap(() => this._accountingProcessesService.deleteAccountingProcessAccount(accountResult.account.id))
			).subscribe(() => this.loadAccounts());
			this._subscriptions.push(sub);
		}
	}

	deleteGroup(group: IAccountingProcessAccountGroup, confirmDeleteDialog: TemplateRef<unknown>) {
		if (group.isAssociated) {
			const message = `${group.groupName} cannot be deleted because it is referenced by one or more accounting processes.`;
			this._promptService.alert("Cannot Delete Account Group", message);
		} else {
			const sub = this._promptService.confirmMini("Confirmation", confirmDeleteDialog, { group }).pipe(
				filter(confirm => confirm),
				tap(() => this.isDeleting.add(group.id)),
				switchMap(() => this._accountingProcessesService.deleteAccountGroup(group.id)),
				switchMap(() => this.loadAccountGroups$)
			).subscribe();
			this._subscriptions.push(sub);
		}
	}

	archiveAccount(accountResult: IAccountingProcessAccountResult, confirmArchiveDialog: TemplateRef<unknown>) {
		const sub = this._promptService.confirmMini("Confirmation", confirmArchiveDialog, accountResult.account).pipe(
			filter(confirm => confirm),
			tap(() => this.isDeleting.add(accountResult.account.id)),
			switchMap(() => this._accountingProcessesService.archiveAccountingProcessAccount(accountResult.account.id))
		).subscribe(() => this.loadAccounts());
		this._subscriptions.push(sub);
	}

	cancelAccount(cancelDialog: TemplateRef<unknown>) {
		if (!this.accountsForm.dirty) {
			this.buildAccountsForm();
			return;
		}

		const sub = this._promptService.confirmMini("Confirmation", cancelDialog)
			.subscribe(confirm => {
				if (confirm) {
					this.buildAccountsForm();
				}
			});

		this._subscriptions.push(sub);
	}

	private clearPartiesValues() {
		this.accountGroupsForm.controls.partyId.setValue(null);
		this.selectedDealPartyCollection = [];
	}

	cancelGroup(cancelGroupDialog: TemplateRef<unknown>) {
		if (!this.accountGroupsForm.dirty) {
			this.clearPartiesValues();
			this.buildAccountGroupsForm();
			return;
		}

		const sub = this._promptService.confirmMini("Confirmation", cancelGroupDialog)
			.subscribe(confirm => {
				if (confirm) {
					this.clearPartiesValues();
					this.buildAccountGroupsForm();
				}
			});

		this._subscriptions.push(sub);
	}

	setIsRlAdmin(isRlAdmin: boolean) {
		this.isRlAdmin = isRlAdmin;
		const adminOnlyColumns = new Set<string | number>(["id", "xref", "alliantAccountGuid", "alliantContactGuid", "sequenceId"]);
		const columns = this.columns.filter(col => isRlAdmin || !adminOnlyColumns.has(col.key));
		this.accountsDataSource.setColumns(columns);
	}

	getDealTemplateDisplayText(templates: ICharacteristicTemplate[]) {
		return templates.map(t => t.templateID + " - " + t.templateName).join("|");
	}

	getPartyDisplayText(dealTemplates: ICharacteristicTemplate[], partyId: number) {
		const dealTemplateIds = dealTemplates.map(t => t.templateID).join("|");
		let dealParties = this.dealPartyCollection[dealTemplateIds] ?? [];
		if (!(dealTemplateIds in this.dealPartyCollection) && dealTemplates?.length > 0) {
			dealParties = this._oneConfig.getParties(CharTypeId.Deal, dealTemplates[0].templateID);
			this.dealPartyCollection[dealTemplateIds] = dealParties;
		}
		const party = dealParties.find(p => p.partyID === partyId);
		return party ? partyId + " - " + party.partyName : partyId + " - Party Not Found";
	}

	getDefaultAccountName(id: string) {
		if (this.defaultAccounts.length > 0) {
			const defaultAccount = this.defaultAccounts?.find(da => da[0] === id);
			if (defaultAccount) {
				return defaultAccount[1];
			}
		}
		return "";
	}

	templateKeyFn = (t: ICharacteristicTemplate) => t;
	templateLabelFn = (t: ICharacteristicTemplate) => t.templateID + " - " + t.templateName;

	ngOnDestroy(): void {
		this._subscriptions.forEach(sub => sub.unsubscribe());
	}

}
